/**
 * Datos fiscales de la factura de compra, con el mismo cálculo que el backend
 * (backend/src/finanzas/comprobante-compra.ts y totalConPercepciones en backend/src/impositivo/percepcion.service.ts):
 * si la pantalla redondeara distinto, el backend rechazaría el total.
 */

import { CONDICION_IVA_EMPRESA, etiquetaCondicionIva } from './condicionIva';

/** Letras de factura que se cargan en compras. */
export const LETRAS_COMPROBANTE = ['A', 'B', 'C', 'M'] as const;

/** Alícuotas de IVA vigentes (en %). */
export const ALICUOTAS_IVA = [21, 10.5, 27, 5, 2.5, 0] as const;

/** Medio centavo: el mismo margen que usa el backend (backend/src/common/montos.ts). */
export const EPSILON_MONTO = 0.005;

export const centavos = (n: number) => Math.round(n * 100) / 100;

/** Tipos de comprobante que se cargan en la pantalla de facturas (el anticipo se elige aparte). */
export const TIPOS_COMPROBANTE = ['factura', 'nota_credito', 'nota_debito'] as const;
export type TipoComprobante = (typeof TIPOS_COMPROBANTE)[number];
export const ETIQUETAS_TIPO_COMPROBANTE: Record<TipoComprobante, string> = {
  factura: 'Factura', nota_credito: 'Nota de crédito', nota_debito: 'Nota de débito',
};
export const esTipoNota = (t: unknown): t is 'nota_credito' | 'nota_debito' => t === 'nota_credito' || t === 'nota_debito';

/** Códigos ARCA por tipo y letra (igual que el backend): factura 1/6/11/51, ND 2/7/12/52, NC 3/8/13/53. */
const CODIGOS_ARCA: Record<TipoComprobante, Record<string, string>> = {
  factura: { A: '1', B: '6', C: '11', M: '51' },
  nota_debito: { A: '2', B: '7', C: '12', M: '52' },
  nota_credito: { A: '3', B: '8', C: '13', M: '53' },
};
export const codigoArcaComprobante = (tipo: string | null | undefined, letra: string | null | undefined): string | null =>
  CODIGOS_ARCA[(esTipoNota(tipo) ? tipo : 'factura') as TipoComprobante][letra ?? ''] ?? null;

/** Número visible del comprobante: "A 00001-00000123" (sin letra: "00001-00000123"; notas: "NC A 00001-00000123"). */
export function numeroComprobanteVisible(letra: string | null | undefined, puntoVenta: number, numero: number, tipo?: string | null): string {
  const pvNumero = `${String(puntoVenta).padStart(5, '0')}-${String(numero).padStart(8, '0')}`;
  const visible = letra ? `${letra} ${pvNumero}` : pvNumero;
  return esTipoNota(tipo) ? `${tipo === 'nota_credito' ? 'NC' : 'ND'} ${visible}` : visible;
}

/** Punto de venta y número de un texto como "0001-00001234" o "A 00001-00000123" (null si no se reconoce). */
export function parsearNumeroComprobante(texto: string): { puntoVenta: number; numero: number } | null {
  const m = /(\d{1,5})\s*-\s*(\d{1,8})\s*$/.exec(texto.trim());
  if (!m) return null;
  const puntoVenta = Number(m[1]);
  const numero = Number(m[2]);
  return puntoVenta >= 1 && numero >= 1 ? { puntoVenta, numero } : null;
}

/** Neto de un ítem: cantidad × precio × (1 − descuento %), a centavos. */
export const netoItem = (cantidad: number, precioUnitario: number, descuentoPorcentaje: number) =>
  centavos(cantidad * precioUnitario * (1 - descuentoPorcentaje / 100));

/** IVA de un neto a una alícuota, a centavos. */
export const ivaDeNeto = (neto: number, alicuota: number) => centavos((neto * alicuota) / 100);

export interface TotalAlicuota { alicuota: number; netoGravado: number; iva: number }

/** Totales por alícuota: suma los netos de cada alícuota y calcula el IVA sobre esa suma. */
export function totalesPorAlicuota(items: { alicuota: number; neto: number }[]): TotalAlicuota[] {
  const netos = new Map<number, number>();
  for (const i of items) netos.set(i.alicuota, centavos((netos.get(i.alicuota) ?? 0) + i.neto));
  return [...netos].map(([alicuota, netoGravado]) => ({ alicuota, netoGravado, iva: ivaDeNeto(netoGravado, alicuota) }));
}

/** Diferencia admitida entre el IVA declarado de una alícuota con ítems y el calculado: un centavo por ítem (redondeo por línea), como el backend. */
export const margenRedondeoIva = (cantidadItems: number) => centavos(0.01 * cantidadItems);

type Monto = number | string | null | undefined;

export interface DesgloseComprobante {
  subtotalNeto?: Monto;
  iva?: Monto;
  netoNoGravado?: Monto;
  exento?: Monto;
  impuestosInternos?: Monto;
  otrosTributos?: Monto;
}

/**
 * Total esperado del comprobante: neto gravado + IVA + no gravado + exento + impuestos internos + otros tributos +
 * percepciones. null sin desglose (ni neto gravado, ni no gravado, ni exento). Los importes que faltan cuentan 0.
 */
export function totalEsperado(d: DesgloseComprobante, percepciones: number): number | null {
  const n = (m: Monto) => Number(m ?? 0) || 0;
  const base = n(d.subtotalNeto) + n(d.netoNoGravado) + n(d.exento);
  if (!(base > EPSILON_MONTO)) return null;
  return centavos(base + n(d.iva) + n(d.impuestosInternos) + n(d.otrosTributos) + percepciones);
}

/**
 * Letras que suele emitir el proveedor a la empresa (Responsable Inscripta) según su condición frente al IVA:
 * RI → A (M también vale); Monotributo o Exento → C. null = sin sugerencia. Nunca bloquea la carga.
 */
export function letrasEsperadasCompra(condicionProveedor: string | null | undefined): string[] | null {
  if (CONDICION_IVA_EMPRESA !== 'RESPONSABLE_INSCRIPTO') return null;
  switch (condicionProveedor) {
    case 'RESPONSABLE_INSCRIPTO': return ['A', 'M'];
    case 'MONOTRIBUTO':
    case 'EXENTO': return ['C'];
    default: return null;
  }
}

/** Letra que se preselecciona en una factura nueva: la primera de las esperadas. */
export const letraSugeridaCompra = (condicionProveedor: string | null | undefined): string | null =>
  letrasEsperadasCompra(condicionProveedor)?.[0] ?? null;

/** Aviso (no error) cuando la letra elegida no es la esperada: "El proveedor es Monotributo: la factura suele ser C". */
export function avisoLetraCompra(condicionProveedor: string | null | undefined, letra: string | null | undefined): string | null {
  const esperadas = letrasEsperadasCompra(condicionProveedor);
  const etiqueta = etiquetaCondicionIva(condicionProveedor);
  if (!esperadas || !etiqueta || esperadas.includes(letra ?? '')) return null;
  return `El proveedor es ${etiqueta}: la factura suele ser ${esperadas.join(' o ')}`;
}
